import { useMemo, useState, type ReactNode } from 'react';
import {
  ApiError,
  fetchTaskDocuments,
  markDocumentCarried,
  markDocumentReturned,
  setTaskDocuments,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { DocumentChecklistPicker } from '../components/DocumentChecklistPicker';
import { formatDateInZone, formatTimeInZone } from '../lib/format';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { hasAnyPermission } from '../lib/navigation';
import {
  checklistPayload,
  documentLabel,
  documentStatusText,
  presetLengkap,
  nextAction,
  pendingWarning,
  summarize,
  type TaskDocument,
} from '../lib/task-documents';
import { ErrorCard } from '../states';

/**
 * Dokumen penagihan pada satu tugas — "tukar faktur".
 *
 * Satu bagian, dua pembaca, dan sengaja BUKAN dua layar terpisah:
 *   - manajer melihat daftar yang ia susun dan sisa yang belum kembali;
 *   - kurir melihat berkas yang ia pegang dan menandainya satu per satu.
 *
 * Keduanya melihat angka yang sama. Memisahkannya menjadi layar manajer dan layar kurir membuat
 * keduanya menghitung sendiri-sendiri, dan perbedaan hitungan itu baru ketahuan saat penagihan
 * ditolak — terlalu jauh dari tempat kesalahannya dibuat.
 */
export function TaskDocumentsSection({ taskId }: { readonly taskId: string }): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  const bolehMenyusun = hasAnyPermission(session.me.permissions, [
    'workforce.task.document.create',
  ]);
  const bolehMenandai = hasAnyPermission(session.me.permissions, [
    'workforce.task.document.update',
  ]);

  const [draft, setDraft] = useState(presetLengkap);
  const [menyusun, setMenyusun] = useState(false);
  const [dokumenRinci, setDokumenRinci] = useState(false);
  const [sibuk, setSibuk] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const { state, reload } = useAsync(
    async () => fetchTaskDocuments(session.auth, taskId).catch((): readonly TaskDocument[] => []),
    [session.auth, taskId],
  );

  if (state.status !== 'ready') return null;

  const documents = state.data;
  const summary = summarize(documents);
  const peringatan = pendingWarning(summary);

  async function jalankan(kerja: () => Promise<unknown>, tanda: string): Promise<void> {
    setSibuk(tanda);
    setError(null);
    try {
      await kerja();
      keys.rotate();
      // Muat ulang dari server, bukan menebak keadaan berikutnya: urutan dibawa→kembali
      // ditegakkan di sana, dan tebakan kita bisa berbeda dari yang benar-benar tersimpan.
      reload();
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setSibuk(null);
    }
  }

  // Belum ada daftar sama sekali: hanya yang berhak menyusun yang melihat apa pun di sini.
  if (documents.length === 0 && !bolehMenyusun) return null;

  return (
    <section className="card">
      <h2>Dokumen penagihan</h2>

      {documents.length === 0 ? (
        <>
          <p className="muted small">
            Belum ada berkas didaftarkan untuk tugas ini. Kurir yang berangkat tanpa daftar tidak
            dapat ditagih berkasnya setelah kembali.
          </p>
          {menyusun ? (
            <>
              <DocumentChecklistPicker
                rows={draft}
                onChange={setDraft}
                disabled={sibuk !== null}
                rinci={dokumenRinci}
                onRinciChange={setDokumenRinci}
              />
              <button
                type="button"
                className="button-primary"
                disabled={sibuk !== null || checklistPayload(draft).length === 0}
                onClick={() =>
                  void jalankan(
                    () =>
                      setTaskDocuments(
                        session.auth,
                        taskId,
                        keys.current(),
                        checklistPayload(draft),
                      ),
                    'simpan',
                  )
                }
              >
                {sibuk === 'simpan' ? 'Menyimpan…' : 'Simpan daftar dokumen'}
              </button>
            </>
          ) : (
            <button type="button" className="link" onClick={() => setMenyusun(true)}>
              Susun daftar dokumen
            </button>
          )}
        </>
      ) : (
        <>
          <p className="small" aria-live="polite">
            {summary.carried} dari {summary.total} berkas dibawa
            {summary.mustReturnTotal > 0
              ? ` · ${summary.returned} dari ${summary.mustReturnTotal} sudah kembali`
              : ''}
          </p>

          {peringatan ? (
            <p className="warning small" role="alert">
              {peringatan}
            </p>
          ) : null}

          <ul className="list">
            {documents.map((document) => {
              const langkah = nextAction(document);
              const waktu = document.returnedAt ?? document.carriedAt;
              return (
                <li key={document.id} className="list-item">
                  <div className="row space-between">
                    <strong>{documentLabel(document.documentType)}</strong>
                    <span className="muted small">
                      {document.mustReturn ? 'Harus kembali' : 'Sekali jalan'}
                    </span>
                  </div>
                  <p className="muted small">
                    {documentStatusText(document)}
                    {waktu
                      ? ` · ${formatDateInZone(waktu, timezone)} ${formatTimeInZone(waktu, timezone)}`
                      : ''}
                  </p>

                  {/*
                   * Tombol hanya muncul untuk langkah yang memang sah berikutnya. Menampilkan
                   * "kembali" pada berkas yang belum dibawa hanya melatih orang mengabaikan
                   * penolakan server.
                   */}
                  {langkah !== null && bolehMenandai ? (
                    <button
                      type="button"
                      className="link"
                      disabled={sibuk !== null}
                      onClick={() =>
                        void jalankan(
                          () =>
                            langkah === 'carried'
                              ? markDocumentCarried(
                                  session.auth,
                                  taskId,
                                  document.id,
                                  keys.current(),
                                )
                              : markDocumentReturned(
                                  session.auth,
                                  taskId,
                                  document.id,
                                  keys.current(),
                                ),
                          document.id,
                        )
                      }
                    >
                      {sibuk === document.id
                        ? 'Menyimpan…'
                        : langkah === 'carried'
                          ? 'Tandai dibawa'
                          : 'Tandai sudah kembali'}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {error ? <ErrorCard error={error} /> : null}
    </section>
  );
}
